import { useMemo, useState } from 'react';
import { Button, Input, Label, TextArea, TextField, toast } from '@heroui/react';
import { Sheet } from '@heroui-pro/react/sheet';
import { Segment } from '@heroui-pro/react/segment';
import { BLOCKS, type BlockId } from '@odd/domain';
import { useCreateManualCase, useOfficeCommunities } from '../lib/queries';
import { useBoardCtx } from './BoardContext';
import { Select, Toggle } from './ui';

const FOUR = [
  ['quePasa', 'Qué pasa'],
  ['queHemosIntentado', 'Qué hemos intentado'],
  ['porQueAtascado', 'Por qué está atascado'],
  ['queProponemos', 'Qué proponemos'],
] as const;
type FourKey = (typeof FOUR)[number][0];
const EMPTY_FOUR: Record<FourKey, string> = {
  quePasa: '',
  queHemosIntentado: '',
  porQueAtascado: '',
  queProponemos: '',
};

/**
 * Alta de un caso que trae el tándem y no está en la mesa ni en la capa de detalle (no lo detecta el sistema).
 * Queda abierto semana a semana hasta que se cierra desde su detalle.
 */
export function ManualCaseSheet({
  isOpen,
  onClose,
  defaultAffId,
  sheetFull = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Tándem desde el que se abre (se preselecciona como AFF) */
  defaultAffId?: string;
  /** El tándem ya tiene 3 casos en su hoja: el caso se registra pero no se añade a la hoja */
  sheetFull?: boolean;
}) {
  const { board, officeId } = useBoardCtx();
  const communities = useOfficeCommunities(officeId, isOpen);
  const create = useCreateManualCase(officeId);

  const [communityId, setCommunityId] = useState('');
  const [title, setTitle] = useState('');
  const [block, setBlock] = useState<BlockId>('S');
  const [light, setLight] = useState<'rojo' | 'ambar'>('rojo');
  const [affId, setAffId] = useState(defaultAffId ?? '');
  const [erpUrl, setErpUrl] = useState('');
  const [note, setNote] = useState('');
  const [withSheet, setWithSheet] = useState(!sheetFull);
  const [four, setFour] = useState(EMPTY_FOUR);
  const [error, setError] = useState<string | null>(null);

  const list = useMemo(() => communities.data ?? [], [communities.data]);
  const communityOptions = useMemo(
    () =>
      list.map((c) => ({
        value: c.id,
        label: c.name,
        description: [c.segment ? `Segmento ${c.segment}` : null, c.affName].filter(Boolean).join(' · '),
      })),
    [list],
  );
  const community = list.find((c) => c.id === communityId);

  const reset = () => {
    setCommunityId('');
    setTitle('');
    setBlock('S');
    setLight('rojo');
    setAffId(defaultAffId ?? '');
    setErpUrl('');
    setNote('');
    setWithSheet(!sheetFull);
    setFour(EMPTY_FOUR);
    setError(null);
  };
  const close = () => {
    reset();
    onClose();
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const aff = affId || community?.affId || '';
    const err = !communityId
      ? 'Elige la comunidad.'
      : title.trim().length < 3
        ? 'Escribe un título corto del caso.'
        : erpUrl && !/^https?:\/\//.test(erpUrl)
          ? 'El enlace a Eleva debe empezar por https://'
          : withSheet && !aff
            ? 'Elige el AFF del tándem que lo trae.'
            : null;
    setError(err);
    if (err) return;
    create.mutate(
      {
        communityId,
        title: title.trim(),
        block,
        light,
        affId: aff || null,
        erpUrl: erpUrl || null,
        note: note || null,
        sheet: withSheet ? { week: board.week, ...four } : null,
      },
      {
        onSuccess: (r) => {
          if (r.sheetError) toast.warning(`Caso registrado, pero no se añadió a la hoja: ${r.sheetError}`);
          else toast.success('Caso registrado');
          close();
        },
        onError: (e2) => setError(e2.message),
      },
    );
  };

  return (
    <Sheet isOpen={isOpen} onOpenChange={(o) => !o && close()} placement="right">
      <Sheet.Backdrop>
        <Sheet.Content className="w-full max-w-xl">
          <Sheet.Dialog>
            <Sheet.CloseTrigger />
            <Sheet.Header>
              <Sheet.Heading className="text-xl font-extrabold text-ink">Añadir un caso</Sheet.Heading>
              <p className="text-sm text-ink-3">
                Para lo que trae el tándem y no está en la mesa ni en la capa de detalle. Queda abierto hasta
                que lo cierres.
              </p>
            </Sheet.Header>
            <Sheet.Body className="pb-8">
              <form onSubmit={submit} className="grid gap-4" noValidate>
                <div className="grid gap-1.5">
                  <span className="text-sm font-medium text-ink">Comunidad</span>
                  <Select
                    label="Comunidad"
                    value={communityId}
                    onChange={(v) => {
                      setCommunityId(v);
                      if (!defaultAffId) setAffId('');
                    }}
                    isDisabled={communities.isLoading}
                    placeholder={communities.isLoading ? 'Cargando comunidades…' : 'Elegir comunidad…'}
                    searchPlaceholder={`Buscar entre ${list.length} comunidades (nombre, AFF, segmento)`}
                    options={communityOptions}
                  />
                </div>

                <TextField value={title} onChange={setTitle}>
                  <Label>Caso</Label>
                  <Input placeholder="El presidente amenaza con cambiar de administrador" />
                </TextField>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-1.5">
                    <span className="text-sm font-medium text-ink">Bloque del orden del día</span>
                    <Select
                      label="Bloque"
                      value={block}
                      onChange={(v) => setBlock(v as BlockId)}
                      options={BLOCKS.map((b) => ({ value: b.id, label: b.name }))}
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <span className="text-sm font-medium text-ink">Semáforo</span>
                    <Segment
                      size="sm"
                      aria-label="Semáforo"
                      selectedKey={light}
                      onSelectionChange={(k) => setLight(k as 'rojo' | 'ambar')}
                    >
                      <Segment.Item id="rojo">● rojo</Segment.Item>
                      <Segment.Item id="ambar">● ámbar</Segment.Item>
                    </Segment>
                  </div>
                </div>

                <div className="grid gap-1.5">
                  <span className="text-sm font-medium text-ink">Tándem (AFF)</span>
                  <Select
                    label="AFF"
                    value={affId || community?.affId || ''}
                    onChange={setAffId}
                    isClearable
                    placeholder="Sin AFF"
                    options={board.tandems.map((t) => ({ value: t.affId, label: t.affName }))}
                  />
                  {community?.affName && (
                    <span className="text-xs text-ink-3">AFF de la comunidad: {community.affName}</span>
                  )}
                </div>

                <TextField value={erpUrl} onChange={setErpUrl}>
                  <Label>Enlace en Eleva (opcional)</Label>
                  <Input placeholder="https://erp.eleva.io/tickets/…" />
                </TextField>
                <TextField value={note} onChange={setNote}>
                  <Label>Nota (opcional)</Label>
                  <TextArea rows={2} />
                </TextField>

                <div className="grid gap-3 rounded-2xl bg-card-2 p-4">
                  <Toggle
                    isSelected={withSheet}
                    onChange={setWithSheet}
                    isDisabled={sheetFull}
                    label={
                      sheetFull
                        ? 'La hoja del tándem ya tiene 3 casos'
                        : 'Lo trae el tándem: añadir a su hoja de esta semana'
                    }
                  />
                  {withSheet &&
                    FOUR.map(([k, label]) => (
                      <TextField key={k} value={four[k]} onChange={(v) => setFour((f) => ({ ...f, [k]: v }))}>
                        <Label>{label}</Label>
                        <TextArea rows={2} />
                      </TextField>
                    ))}
                </div>

                {error && (
                  <p role="alert" className="text-sm font-semibold text-bad">
                    {error}
                  </p>
                )}
                <div className="flex gap-2">
                  <Button type="submit" isPending={create.isPending}>
                    Registrar caso
                  </Button>
                  <Button variant="ghost" onPress={close}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </Sheet.Body>
          </Sheet.Dialog>
        </Sheet.Content>
      </Sheet.Backdrop>
    </Sheet>
  );
}
